/**
 * CapiTestEventModeForVerificationPage — log + inspect Meta CAPI events sent in
 * test mode for wire-up verification. Events with test_mode=1 are flagged and
 * excluded from production analytics.
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	useCapiTestEventCreate,
	useCapiTestEventList,
} from "./hooks";
import type { TestModeFilter } from "./api";

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-6 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-500/25";
const SELECT =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-500/25";
const BTN =
	"inline-flex items-center gap-1.5 rounded-[6px] border border-green-700 bg-green-600 px-3 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"inline-flex items-center gap-1.5 rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50";
const PILL_TEST =
	"inline-flex items-center gap-1 rounded-[6px] border border-green-200 bg-green-50 px-2 py-0.5 text-[11px] font-medium text-green-700";
const PILL_LIVE =
	"inline-flex items-center gap-1 rounded-[6px] border border-zinc-200 bg-white px-2 py-0.5 text-[11px] font-medium text-zinc-600";
const TAB =
	"rounded-[6px] border px-3 py-1.5 text-xs font-medium transition-colors";
const TAB_ON = `${TAB} border-green-600 bg-green-50 text-green-700`;
const TAB_OFF = `${TAB} border-zinc-200 bg-white text-zinc-600 hover:border-zinc-300`;

function Dot({ active }: { active: boolean }) {
	return (
 <svg
 aria-hidden="true"
 viewBox="0 0 8 8"
 className="h-2 w-2"
 fill={active ? "currentColor" : "none"}
 stroke="currentColor"
 strokeWidth="1.5"
 >
 <circle cx="4" cy="4" r="3" />
 </svg>
	);
}

function formatTs(seconds: number): string {
	if (!seconds || Number.isNaN(seconds)) return "—";
	try {
 return new Date(seconds * 1000).toISOString().replace("T","").slice(0, 19);
	} catch {
 return String(seconds);
	}
}

export function CapiTestEventModeForVerificationPage() {
	const [filter, setFilter] = useState<TestModeFilter>("all");
	const listQuery = useCapiTestEventList(filter);
	const createMut = useCapiTestEventCreate();

	const [eventId, setEventId] = useState("");
	const [eventName, setEventName] = useState("Purchase");
	const [testMode, setTestMode] = useState("1");
	const [verificationCode, setVerificationCode] = useState("");

	const submitReady = eventId.trim().length > 0 && eventName.trim().length > 0;

	const handleCreate = (e: FormEvent) => {
 e.preventDefault();
 if (!submitReady) return;
 createMut.mutate(
 {
 event_id: eventId.trim(),
 event_name: eventName.trim(),
 test_mode: Number(testMode),
 verification_code: verificationCode.trim() || undefined,
 payload: { source: "dash", logged_from: "verification-panel" },
 },
 {
 onSuccess: () => {
 setEventId("");
 setVerificationCode("");
 },
 },
 );
	};

	const items = listQuery.data?.items ?? [];
	const counts = useMemo(() => {
 let testN = 0;
 let liveN = 0;
 for (const row of items) {
 if (row.test_mode === 1) testN++;
 else liveN++;
 }
 return { total: items.length, test: testN, live: liveN };
	}, [items]);

	return (
 <main
 data-testid="capi-test-event-mode-for-verification-page"
 className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
 >
 <header className="flex flex-col gap-2">
 <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
 Integration / Meta CAPI
 </p>
 <h1 className="text-3xl font-semibold text-green-700">
 CAPI test event verification
 </h1>
 <p className="max-w-2xl text-sm text-zinc-600">
 Log Meta CAPI events in test mode to verify wire-up before switching
 the integration live. Events flagged <code className="rounded bg-green-50 px-1 py-0.5 text-[11px] text-green-700">test_mode=1</code> are
 stored separately and excluded from production analytics.
 </p>
 </header>

 <section className="grid grid-cols-3 gap-4" aria-label="Event counts">
 <div className={CARD} data-testid="stat-total">
 <p className="text-xs font-medium uppercase tracking-wider text-zinc-500">
 Total
 </p>
 <p className="mt-1 text-2xl font-semibold text-zinc-900">
 {counts.total}
 </p>
 </div>
 <div className={CARD} data-testid="stat-test">
 <p className="text-xs font-medium uppercase tracking-wider text-zinc-500">
 Test mode
 </p>
 <p className="mt-1 text-2xl font-semibold text-green-700">
 {counts.test}
 </p>
 </div>
 <div className={CARD} data-testid="stat-live">
 <p className="text-xs font-medium uppercase tracking-wider text-zinc-500">
 Live
 </p>
 <p className="mt-1 text-2xl font-semibold text-zinc-900">
 {counts.live}
 </p>
 </div>
 </section>

 <section className={CARD} aria-label="Log verification event">
 <h2 className="text-green-700 font-semibold text-base font-semibold text-zinc-900">Log event</h2>
 <p className="mt-1 text-xs text-zinc-500">
 Event id must be unique per tenant. Duplicates are silently dropped.
 </p>
 <form
 onSubmit={handleCreate}
 className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2"
 data-testid="capi-test-event-mode-for-verification-create-form"
 >
 <label className="flex flex-col gap-1 text-xs font-medium text-zinc-700">
 Event id
 <input
 type="text"
 className={INPUT}
 placeholder="evt_abc_123"
 value={eventId}
 onChange={(e) => setEventId(e.currentTarget.value)}
 disabled={createMut.isPending}
 data-testid="event-id-input"
 />
 </label>
 <label className="flex flex-col gap-1 text-xs font-medium text-zinc-700">
 Event name
 <input
 type="text"
 className={INPUT}
 placeholder="Purchase"
 value={eventName}
 onChange={(e) => setEventName(e.currentTarget.value)}
 disabled={createMut.isPending}
 data-testid="event-name-input"
 />
 </label>
 <label className="flex flex-col gap-1 text-xs font-medium text-zinc-700">
 Mode
 <select
 className={SELECT}
 value={testMode}
 onChange={(e) => setTestMode(e.currentTarget.value)}
 disabled={createMut.isPending}
 data-testid="test-mode-select"
 >
 <option value="1">Test mode</option>
 <option value="0">Live</option>
 </select>
 </label>
 <label className="flex flex-col gap-1 text-xs font-medium text-zinc-700">
 Verification code (optional)
 <input
 type="text"
 className={INPUT}
 placeholder="TEST1234"
 value={verificationCode}
 onChange={(e) => setVerificationCode(e.currentTarget.value)}
 disabled={createMut.isPending}
 data-testid="verification-code-input"
 />
 </label>
 <div className="sm:col-span-2">
 <button
 type="submit"
 className={BTN}
 disabled={createMut.isPending || !submitReady}
 data-testid="capi-test-event-mode-for-verification-submit"
 >
 <Dot active={!createMut.isPending} />
 {createMut.isPending ? "Logging" : "Log event"}
 </button>
 </div>
 </form>
 {createMut.error ? (
 <p
 className="mt-3 text-xs text-red-600"
 role="alert"
 data-testid="create-error"
 >
 {createMut.error.message}
 </p>
 ) : null}
 {createMut.isSuccess ? (
 <p
 className="mt-3 text-xs text-green-700"
 role="status"
 data-testid="create-success"
 >
 Logged event {createMut.data?.event_id} (test_mode=
 {createMut.data?.test_mode}).
 </p>
 ) : null}
 </section>

 <section
 className={CARD}
 aria-label="Logged verification events"
 >
 <div className="flex flex-wrap items-center justify-between gap-3">
 <h2 className="text-green-700 font-semibold text-base font-semibold text-zinc-900">Recent events</h2>
 <div
 className="flex items-center gap-2"
 role="tablist"
 aria-label="Filter by mode"
 >
 {(["all", "test", "live"] as const).map((f) => (
 <button
 key={f}
 type="button"
 role="tab"
 aria-selected={filter === f}
 className={filter === f ? TAB_ON : TAB_OFF}
 onClick={() => setFilter(f)}
 data-testid={`filter-${f}`}
 >
 {f === "all" ? "All" : f === "test" ? "Test mode" : "Live"}
 </button>
 ))}
 </div>
 </div>

 {listQuery.isLoading ? (
 <p
 className="mt-4 text-sm text-zinc-500"
 data-testid="capi-test-event-mode-for-verification-loading"
 >
 Loading events.
 </p>
 ) : listQuery.error ? (
 <p
 className="mt-4 text-sm text-red-600"
 role="alert"
 data-testid="list-error"
 >
 {listQuery.error.message}
 </p>
 ) : items.length === 0 ? (
 <p
 className="mt-4 text-sm text-zinc-500"
 data-testid="capi-test-event-mode-for-verification-empty"
 >
 No events logged yet. Log one above to verify your CAPI wire-up.
 </p>
 ) : (
 <div className="mt-4 overflow-x-auto">
 <table
 className="w-full min-w-[640px] table-auto border-collapse text-left text-sm"
 data-testid="capi-test-event-mode-for-verification-list"
 >
 <thead>
 <tr className="border-b border-zinc-200 text-[11px] font-medium uppercase tracking-wider text-zinc-500">
 <th className="py-2 pr-3">Event id</th>
 <th className="py-2 pr-3">Name</th>
 <th className="py-2 pr-3">Mode</th>
 <th className="py-2 pr-3">Code</th>
 <th className="py-2 pr-3">Received</th>
 </tr>
 </thead>
 <tbody>
 {items.map((row) => (
 <tr
 key={row.id}
 className="border-b border-zinc-100 last:border-b-0"
 data-testid={`capi-test-event-mode-for-verification-row-${row.event_id}`}
 >
 <td className="py-2 pr-3 font-mono text-xs text-zinc-700">
 {row.event_id}
 </td>
 <td className="py-2 pr-3 text-zinc-900">{row.event_name}</td>
 <td className="py-2 pr-3">
 {row.test_mode === 1 ? (
 <span className={PILL_TEST}>
 <Dot active={true} />
 test_mode
 </span>
 ) : (
 <span className={PILL_LIVE}>
 <Dot active={false} />
 live
 </span>
 )}
 </td>
 <td className="py-2 pr-3 font-mono text-xs text-zinc-600">
 {row.verification_code ?? "—"}
 </td>
 <td className="py-2 pr-3 font-mono text-xs text-zinc-500">
 {formatTs(row.received_at)}
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 )}

 <div className="mt-4 flex justify-end">
 <button
 type="button"
 className={BTN_GHOST}
 onClick={() => listQuery.refetch()}
 disabled={listQuery.isFetching}
 data-testid="refresh-list"
 >
 {listQuery.isFetching ? "Refreshing" : "Refresh"}
 </button>
 </div>
 </section>
 </main>
	);
}

export default CapiTestEventModeForVerificationPage;
